// WhatsApp API Service - Modularized WhatsApp messaging API methods
import BaseApiService from './BaseApiService';

class WhatsAppApiService extends BaseApiService {
  constructor() {
    super(); // Initialize BaseApiService with default PHP API URL and timeout
  }

  /**
   * Send a single WhatsApp message
   * @param {string} phoneNumber - Recipient phone number
   * @param {string} message - Message content
   * @param {number|null} schoolId - Optional school ID
   */
  async sendWhatsAppMessage(phoneNumber, message, schoolId = null) {
    try {
      const response = await this.makeRequest('whatsapp/send.php', {
        method: 'POST',
        body: JSON.stringify({
          phone_number: phoneNumber,
          message: message,
          school_id: schoolId,
        }),
      });

      return this.handleResponse(response, 'Message sent successfully', 'Failed to send message');
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Send bulk WhatsApp messages
   * @param {Array} messages - Array of message objects
   */
  async sendBulkWhatsAppMessages(messages) {
    try {
      const response = await this.makeRequest('whatsapp/send-bulk.php', {
        method: 'POST',
        body: JSON.stringify({
          messages: messages,
        }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Messages sent successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to send messages',
          message: response.api_remark || 'Failed to send messages',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  /**
   * Send bulk WhatsApp messages to multiple schools
   * @param {Array<number>} schoolIds - Array of school IDs
   * @param {string} message - Message content
   */
  async sendBulkWhatsAppToSchools(schoolIds, message) {
    try {
      const response = await this.makeRequest('whatsapp/send-bulk.php', {
        method: 'POST',
        body: JSON.stringify({
          school_ids: schoolIds,
          message: message,
        }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Messages sent successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to send messages',
          message: response.api_remark || 'Failed to send messages',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  /**
   * Schedule a WhatsApp message
   * @param {string} phoneNumber - Recipient phone number
   * @param {string} message - Message content
   * @param {string} scheduledDateTime - Scheduled date and time (ISO format)
   * @param {number|null} schoolId - Optional school ID
   */
  async scheduleWhatsAppMessage(phoneNumber, message, scheduledDateTime, schoolId = null) {
    try {
      const response = await this.makeRequest('whatsapp/schedule.php', {
        method: 'POST',
        body: JSON.stringify({
          phone_number: phoneNumber,
          message: message,
          scheduled_datetime: scheduledDateTime,
          school_id: schoolId,
        }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Message scheduled successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to schedule message',
          message: response.api_remark || 'Failed to schedule message',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  /**
   * Schedule bulk WhatsApp messages to multiple schools
   * @param {Array<number>} schoolIds - Array of school IDs
   * @param {string} message - Message content
   * @param {string} scheduledDateTime - Scheduled date and time (ISO format)
   */
  async scheduleBulkWhatsAppToSchools(schoolIds, message, scheduledDateTime) {
    try {
      const response = await this.makeRequest('whatsapp/schedule.php', {
        method: 'POST',
        body: JSON.stringify({
          school_ids: schoolIds,
          message: message,
          scheduled_datetime: scheduledDateTime,
        }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Messages scheduled successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to schedule messages',
          message: response.api_remark || 'Failed to schedule messages',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  /**
   * Get schools for WhatsApp messaging
   * @param {string} searchTerm - Search term for filtering schools
   * @param {string} statusFilter - Status filter ('all', 'billed', 'demo', etc.)
   * @param {number} limit - Maximum number of records to return
   * @param {number} offset - Offset for pagination
   */
  async getWhatsAppSchools(searchTerm = '', statusFilter = 'all', limit = 100, offset = 0) {
    try {
      const params = this.createSearchParams({
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : null,
        limit: limit,
        offset: offset,
      });

      const response = await this.makeRequest(`whatsapp/getSchools.php?${params.toString()}`);

      return this.handleResponse(response, 'Schools retrieved successfully', 'Failed to retrieve schools');
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get WhatsApp message templates
   */
  async getWhatsAppTemplates() {
    try {
      const response = await this.makeRequest('whatsapp/templates.php?action=get');

      return this.handleResponse(response, 'Templates retrieved successfully', 'Failed to retrieve templates');
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Save a WhatsApp message template
   * @param {string} name - Template name
   * @param {string} content - Template content
   * @param {string} category - Template category (default: 'Custom')
   */
  async saveWhatsAppTemplate(name, content, category = 'Custom') {
    try {
      const response = await this.makeRequest('whatsapp/templates.php?action=save', {
        method: 'POST',
        body: JSON.stringify({
          name: name,
          content: content,
          category: category,
        }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Template saved successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to save template',
          message: response.api_remark || 'Failed to save template',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  /**
   * Add WhatsApp messages to queue
   * @param {Array} schools - Array of school objects with school_code, contact_number, etc.
   * @param {string} message - Message content
   * @param {Object} options - Optional parameters (waba_id, template_id, template_name, message_type, scheduled_at)
   */
  async addMessagesToQueue(schools, message, options = {}) {
    try {
      const response = await this.makeRequest('whatsapp/addToQueue.php', {
        method: 'POST',
        body: JSON.stringify({
          schools: schools,
          message: message,
          waba_id: options.waba_id || null,
          template_id: options.template_id || null,
          template_name: options.template_name || null,
          message_type: options.message_type || 'text',
          scheduled_at: options.scheduled_at || null,
        }),
      });

      // Log the response
      console.log("[whatsappApiService] addMessagesToQueue Response:", response);

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data,
          message: 'Messages added to queue successfully',
        };
      } else {
      return {
        success: false,
        error: response.api_remark || 'Failed to add messages to queue',
        errors: response.errors || [],
        message: response.api_remark || 'Failed to add messages to queue',
      };
      }
    } catch (error) {
      console.error("[whatsappApiService] addMessagesToQueue Error:", error);
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  /**
   * Get WhatsApp message queue by group (pending | sent | failed).
   */
  async getMessageQueue(group = 'pending', limit = 100, offset = 0) {
    try {
      const params = this.createSearchParams({
        group,
        limit,
        offset,
      });

      const response = await this.makeRequest(`whatsapp/queue.php?${params.toString()}`);

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data,
          message: 'Queue retrieved successfully',
        };
      }

      return {
        success: false,
        error: response.api_remark || 'Failed to retrieve queue',
        message: response.api_remark || 'Failed to retrieve queue',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * SSE stream URL for live inbox events (EventSource).
   */
  getInboxEventsUrl(sinceId = 0) {
    const baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    const params = new URLSearchParams({ since_id: String(sinceId || 0) });
    return `${baseURL}/whatsapp/events.php?${params.toString()}`;
  }

  /**
   * Get inbox stats (totals / unread).
   */
  async getInboxStats() {
    try {
      const response = await this.makeRequest('whatsapp/inbox.php?action=stats');
      return this.handleResponse(response, 'Inbox stats retrieved', 'Failed to retrieve inbox stats');
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get flat inbox messages (legacy list).
   */
  async getInboxMessages(limit = 100, offset = 0, options = {}) {
    try {
      const params = this.createSearchParams({
        action: 'messages',
        limit,
        offset,
        search: options.search || null,
        unread_only: options.unreadOnly ? '1' : null,
      });
      const response = await this.makeRequest(`whatsapp/inbox.php?${params.toString()}`);

      if (response.api_status === 'OK') {
        return { success: true, data: response.data, message: 'Inbox retrieved successfully' };
      }

      return {
        success: false,
        error: response.api_remark || 'Failed to retrieve inbox',
        message: response.api_remark || 'Failed to retrieve inbox',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get inbox conversations grouped by contact.
   */
  async getInboxConversations(options = {}) {
    try {
      const params = this.createSearchParams({
        action: 'conversations',
        limit: options.limit ?? 50,
        offset: options.offset ?? 0,
        search: options.search || null,
        unread_only: options.unreadOnly ? '1' : null,
      });
      const response = await this.makeRequest(`whatsapp/inbox.php?${params.toString()}`);

      if (response.api_status === 'OK') {
        return { success: true, data: response.data, message: 'Conversations retrieved successfully' };
      }

      return {
        success: false,
        error: response.api_remark || 'Failed to retrieve conversations',
        message: response.api_remark || 'Failed to retrieve conversations',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get full thread for a contact (inbound + outbound).
   */
  async getInboxThread(contactNumber, limit = 200) {
    try {
      const params = this.createSearchParams({
        action: 'thread',
        contact: contactNumber,
        limit,
      });
      const response = await this.makeRequest(`whatsapp/inbox.php?${params.toString()}`);

      if (response.api_status === 'OK') {
        return { success: true, data: response.data, message: 'Thread retrieved successfully' };
      }

      return {
        success: false,
        error: response.api_remark || 'Failed to retrieve thread',
        message: response.api_remark || 'Failed to retrieve thread',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Mark inbox messages as read.
   */
  async markInboxRead({ contact, ids } = {}) {
    try {
      const response = await this.makeRequest('whatsapp/inbox.php', {
        method: 'POST',
        body: JSON.stringify({
          action: 'mark_read',
          contact_number: contact || null,
          ids: ids || [],
        }),
      });

      if (response.api_status === 'OK') {
        return { success: true, data: response.data, message: 'Marked as read' };
      }

      return {
        success: false,
        error: response.api_remark || 'Failed to mark as read',
        message: response.api_remark || 'Failed to mark as read',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Send a reply from inbox (queue + process).
   */
  async sendInboxReply(contactNumber, message, schoolCode = null) {
    try {
      const response = await this.makeRequest('whatsapp/inbox.php', {
        method: 'POST',
        body: JSON.stringify({
          action: 'reply',
          contact_number: contactNumber,
          message,
          school_code: schoolCode,
        }),
      });

      if (response.api_status === 'OK') {
        return { success: true, data: response.data, message: 'Reply sent' };
      }

      return {
        success: false,
        error: response.api_remark || 'Failed to send reply',
        message: response.api_remark || 'Failed to send reply',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Process pending WhatsApp queue (same logic as server cron whatsapp_sender.php).
   * @param {Object} options - batchSize, maxBatches, sleepSeconds
   */
  async processMessageQueue(options = {}) {
    try {
      const response = await this.makeRequest('whatsapp/processQueue.php', {
        method: 'POST',
        body: JSON.stringify({
          batch_size: options.batchSize ?? 10,
          max_batches: options.maxBatches ?? 50,
          sleep_seconds: options.sleepSeconds ?? 0,
        }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data,
          message: response.api_remark || 'Queue processed',
        };
      }

      return {
        success: false,
        error: response.api_remark || 'Failed to process message queue',
        data: response.data,
        message: response.api_remark || 'Failed to process message queue',
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }
}

// Create and export a singleton instance
const whatsappApiService = new WhatsAppApiService();
export default whatsappApiService;
